import { apiFetch } from "@pontimed/api-client";
import { queryOptions } from "@tanstack/react-query";

/**
 * Shared query options for `/api/account/sidebar-data` — the catch-all
 * authenticated payload that drives header / side-nav / bottom-tab-bar /
 * workspace switcher badges + counts. Six layout components plus a handful
 * of account index routes consume it; the route loader on `_authenticated`
 * prefetches it so navigating into the account area never starts cold.
 *
 * The type is intentionally a loose superset of every consumer's local
 * `interface SidebarData` (defined inline in each layout component). Cleanup
 * of those duplicate locals is a separate refactor — keep them in sync by
 * extending this type when adding new fields server-side.
 */
export interface SidebarData {
  subscriptionPlan?: string | null;
  /** Tier word off the plan code (`provider_expert` → "expert"). Ranking only —
   *  never print it: D2 renamed `provider_expert` to "Premium". */
  providerPlanTier?: string | null;
  /** Plan code behind `providerPlanTier`, e.g. `provider_expert`. */
  providerPlanCode?: string | null;
  /** Display name resolved server-side from `pricing_plans.name` — THE string to
   *  print in plan chips and billing cards. */
  providerPlanLabel?: string | null;
  providerPlanName?: Record<string, string> | null;
  facilityPlanTiers?: Record<string, string>;
  vaultProtection?: string | null;
  countryReferralEnabled?: boolean;
  lastReferralAt?: string | null;
  notificationCount?: number;
  actionRequiredNotificationCount?: number;
  informationalNotificationCount?: number;
  pendingInvitationCount?: number;
  pendingAffiliationsCount?: number;
  profile?: {
    appointmentCount?: number;
    favoriteCount?: number;
    documentCount?: number;
    waitlistCount?: number;
    savedSearchCount?: number;
  };
  provider?: {
    profileStatus: string;
    isVerified: boolean;
    completeness: number;
    ratingAvg: number | null;
    reviewCount: number;
    appointmentCount: number;
    facilityCount: number;
  };
  facilityStats?: Record<
    string,
    { appointmentCount: number; providerCount: number; staffCount: number; completeness: number }
  >;
  workspacePhotos?: {
    userPhotoUrl?: string | null;
    providerPhotoUrl?: string | null;
    facilityPhotos?: Record<string, string | null>;
  };
  claimedBusinesses?: Array<{ id: string; nameAscii: string; category?: string | null }>;
  /**
   * 2FA setup window (plan D9). ISO deadline while the user is NOT enrolled in
   * 2FA and still inside the 7 days that start at the later of their account
   * and their provider profile; `null` otherwise. Two consumers:
   * `TwoFactorSetupWindowBanner` (the countdown) and `useMfaEnforcer` (which
   * must NOT hard-redirect to the enrollment wizard while this is non-null).
   */
  twoFactorSetupDeadline?: string | null;
}

export const sidebarDataQueryKey = ["api", "/api/account/sidebar-data"] as const;

export const sidebarDataQueryOptions = queryOptions({
  queryKey: sidebarDataQueryKey,
  queryFn: () => apiFetch.get<SidebarData>("/api/account/sidebar-data"),
});
